/**
 * AutomationTab Component
 *
 * Main container component for automation in AI Studio.
 * Displays a list of automation rules and execution history.
 */

import { memo, useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { IAutomationTabProps, IAutomationRule } from '@/@types/automation';
import type { Id } from '../../../convex/_generated/dataModel';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Clock, History, ListTodo } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AutomationRuleCard } from './AutomationRuleCard';
import { AutomationRuleForm } from './AutomationRuleForm';
import { ExecutionHistory } from './ExecutionHistory';
import { TestProgressModal } from './TestProgressModal';

export const AutomationTab = memo(function AutomationTab({ className }: IAutomationTabProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<Id<'automationRules'> | null>(null);
  const [activeTab, setActiveTab] = useState<'rules' | 'history'>('rules');

  // Test run modal state
  const [testExecutionId, setTestExecutionId] = useState<Id<'automationExecutions'> | null>(null);
  const [testRuleName, setTestRuleName] = useState('');

  // Fetch automation rules
  const rules = useQuery(api.automation.rules.list, {});
  const isLoading = rules === undefined;

  // Mutations for rule management
  const updateRule = useMutation(api.automation.rules.update);
  const deleteRule = useMutation(api.automation.rules.remove);
  const testRunMutation = useMutation(api.automation.rules.testRun);

  const handleCreateClick = useCallback(() => setIsCreateOpen(true), []);

  const handleEditClick = useCallback((ruleId: Id<'automationRules'>) => {
    setEditingRuleId(ruleId);
  }, []);

  const handleFormClose = useCallback(() => {
    setIsCreateOpen(false);
    setEditingRuleId(null);
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as 'rules' | 'history');
  }, []);

  const handleToggleStatus = useCallback(
    async (ruleId: Id<'automationRules'>) => {
      const rule = rules?.find((r: IAutomationRule) => r._id === ruleId);
      if (!rule) return;
      const newStatus = rule.status === 'active' ? 'paused' : 'active';
      await updateRule({ ruleId, status: newStatus });
    },
    [rules, updateRule]
  );

  const handleDeleteRule = useCallback(
    async (ruleId: Id<'automationRules'>) => {
      await deleteRule({ ruleId });
    },
    [deleteRule]
  );

  const handleTestRun = useCallback(
    async (ruleId: Id<'automationRules'>) => {
      const rule = rules?.find((r: IAutomationRule) => r._id === ruleId);
      if (!rule) return;

      try {
        const executionId = await testRunMutation({ ruleId });
        setTestRuleName(rule.name);
        setTestExecutionId(executionId);
      } catch (error) {
        console.error('Failed to start test run:', error);
      }
    },
    [rules, testRunMutation]
  );

  const handleCloseTestModal = useCallback(() => {
    setTestExecutionId(null);
    setTestRuleName('');
  }, []);

  return (
    <div className={cn('flex flex-col gap-6 p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Automation</h2>
          <p className="text-muted-foreground">
            Schedule automatic content generation and publishing
          </p>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          Create Rule
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="rules" className="gap-2">
            <ListTodo className="h-4 w-4" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-6">
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {['skeleton-rule-a', 'skeleton-rule-b'].map(key => (
                <Card key={key}>
                  <CardHeader>
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-60" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : rules && rules.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {rules.map((rule: IAutomationRule) => (
                <AutomationRuleCard
                  key={rule._id}
                  rule={rule}
                  onEdit={handleEditClick}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleDeleteRule}
                  onTestRun={handleTestRun}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardHeader className="text-center">
                <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
                <CardTitle>No Automation Rules</CardTitle>
                <CardDescription>
                  Create your first automation rule to start scheduling content
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button onClick={handleCreateClick}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Rule
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <ExecutionHistory />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Form Dialog */}
      <AutomationRuleForm
        open={isCreateOpen || editingRuleId !== null}
        onOpenChange={handleFormClose}
        ruleId={editingRuleId ?? undefined}
        onSuccess={handleFormClose}
      />

      {/* Test Progress Modal */}
      <TestProgressModal
        isOpen={testExecutionId !== null}
        onClose={handleCloseTestModal}
        executionId={testExecutionId}
        ruleName={testRuleName}
      />
    </div>
  );
});
